/**
 * SessionDeliberationThread — the session thread vector of the Deliberation
 * Record (Doc 21 §6.4). One input, one list, realtime via the deliberation
 * store's channel. Not a chat product: it is the record of what was said
 * while the decision was being made.
 */
import { useEffect, useMemo, useState } from 'react';
import { MessagesSquare, Loader2 } from 'lucide-react';
import { useDeliberationStore } from '../../../stores/prioritization/deliberationStore';
import { useUserStore } from '../../../stores/prioritization/userStore';

interface SessionDeliberationThreadProps {
  sessionId: string;
  workspaceId: string;
  canWrite: boolean;
}

export function SessionDeliberationThread({ sessionId, workspaceId, canWrite }: SessionDeliberationThreadProps) {
  const messages = useDeliberationStore((s) => s.messages);
  const loaded = useDeliberationStore((s) => s.loadedSessions[sessionId]);
  const fetchForSession = useDeliberationStore((s) => s.fetchForSession);
  const subscribe = useDeliberationStore((s) => s.subscribe);
  const unsubscribe = useDeliberationStore((s) => s.unsubscribe);
  const postMessage = useDeliberationStore((s) => s.postMessage);
  const userId = useUserStore((s) => s.userId);
  const userName = useUserStore((s) => s.userName);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!loaded) void fetchForSession(sessionId);
    subscribe(sessionId);
    return () => unsubscribe();
  }, [sessionId, loaded, fetchForSession, subscribe, unsubscribe]);

  const thread = useMemo(() => messages.filter((m) => m.sessionId === sessionId), [messages, sessionId]);

  const send = async () => {
    if (!userId) return;
    setBusy(true);
    setError('');
    const res = await postMessage({ sessionId, workspaceId, authorId: userId, authorName: userName || 'Member', content: draft });
    setBusy(false);
    if (!res.ok) {
      setError(res.error ?? 'Could not post.');
      return;
    }
    setDraft('');
  };

  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-[var(--surface)]"
      >
        <span className="inline-flex items-center gap-2 text-sm font-medium text-[var(--text-sec)]">
          <MessagesSquare className="h-4 w-4 text-[var(--text-ter)]" />
          Session thread
          <span className="text-xs text-[var(--text-ter)]">{thread.length === 0 ? 'nothing recorded yet' : `${thread.length} message${thread.length === 1 ? '' : 's'}`}</span>
        </span>
        <span className="text-xs text-[var(--text-ter)]">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <div className="border-t border-[var(--rule)] px-4 py-3 space-y-3">
          {thread.length === 0 ? (
            <p className="text-xs text-[var(--text-ter)] italic">No discussion has been recorded for this session.</p>
          ) : (
            <ul className="space-y-2 max-h-72 overflow-y-auto">
              {thread.map((m) => (
                <li key={m.id} className="text-sm">
                  <span className="font-medium text-[var(--text-pri)]">{m.authorName || 'Member'}</span>
                  <span className="text-xs text-[var(--text-ter)] ml-2">{new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  <p className="text-[var(--text-sec)] whitespace-pre-wrap">{m.content}</p>
                </li>
              ))}
            </ul>
          )}
          {canWrite && (
            <div className="flex items-start gap-2">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void send();
                }}
                rows={2}
                placeholder="Say it for the record (⌘↵ to post)"
                className="flex-1 border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-1.5 text-sm text-[var(--text-pri)] outline-none focus:border-[var(--slate)] resize-none"
                disabled={busy}
              />
              <button
                type="button"
                onClick={() => void send()}
                disabled={busy || !draft.trim()}
                className="px-3 py-1.5 text-sm font-medium text-white bg-[var(--slate)] disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Post'}
              </button>
            </div>
          )}
          {error && <p className="text-xs text-[var(--coral)]">{error}</p>}
        </div>
      )}
    </div>
  );
}
